import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DateInputRange, IconLoader } from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import Card from '../../../components/partial/card/card';
import BaseStatistic from '../../../components/partial/statistic/base-statistic';
import Currencies from '../../../components/partial/statistic/currencies';
import ProfitByCustomer from '../../../components/partial/statistic/profit-by-customer';
import { useLazyGetCustomerStatsQuery } from '../store/services/customers';
import { setType } from '../store/slices/statistic';
import { CustomerType } from '../types';

import cn from '../customers.module.css';
import cardStyle from '../../../components/partial/card/card.module.css';

const Statistic = ({
  customerType,
}: {
  customerType: CustomerType;
}): React.JSX.Element => {
  const [baseStatisticDates, setBaseStatisticsDate] = useState<Date[]>([]);
  const [profitByCustomerDates, setProfitByCustomerDate] = useState<Date[]>([]);

  const { baseStatistic, profitByCustomer, type } = useAppSelector(
    (state) => state.statistic,
  );

  const {
    corporateCustomers: {
      info: {
        general: {
          baseInformation: corporateBaseInformation,
          balances: corporateBalances,
          totalBalance: corporateTotalBalance,
        },
      },
    },
    individualCustomers: {
      info: {
        general: {
          baseInformation: individualBaseInformation,
          balances: individualBalances,
          totalBalance: individualTotalBalance,
        },
      },
    },
    customersDictionaries: { customersTransactionTypes, baseCurrency },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const { customerId } = useParams();

  const [getBaseStatistics, { isLoading, isFetching, isSuccess }] =
    useLazyGetCustomerStatsQuery();
  const [
    getProfitByCustomer,
    { isLoading: profitIsLoading, isFetching: profitIsFetching },
  ] = useLazyGetCustomerStatsQuery();

  const getBaseStatisticsAction = useCallback((query?: string) => {
    getBaseStatistics({ id: customerId as string, query });
  }, []);

  const getProfitByCustomerAction = useCallback((query?: string) => {
    getProfitByCustomer({ id: customerId as string, query });
  }, []);

  const currentBalances =
    customerType === CustomerType.INDIVIDUAL
      ? individualBalances
      : corporateBalances;

  const currentTotalBalance =
    customerType === CustomerType.INDIVIDUAL
      ? individualTotalBalance
      : corporateTotalBalance;

  useEffect(() => {
    if (!baseStatistic && !profitByCustomer && customerId) {
      getBaseStatisticsAction();
    }
  }, [baseStatistic, customerId, profitByCustomer]);

  useEffect(() => {
    let title: string;

    const companyName = localStorage.getItem('companyName');

    if (customerType === CustomerType.INDIVIDUAL) {
      if (
        individualBaseInformation?.firstName ||
        individualBaseInformation?.lastName
      ) {
        title = `${individualBaseInformation?.firstName} ${individualBaseInformation?.lastName}: Statistics`;
      } else {
        title = 'Statistics';
      }
    } else if (corporateBaseInformation?.name) {
      title = `${corporateBaseInformation?.name}: Statistics`;
    } else {
      title = 'Statistics';
    }

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [
    individualBaseInformation?.firstName,
    individualBaseInformation?.lastName,
    corporateBaseInformation?.name,
  ]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(setType(undefined));
    }
  }, [isSuccess]);

  return (
    <div className={cn.customerStatistic}>
      <Card
        title="Base statistics"
        isLoading={isLoading}
        className={cardStyle.baseStatisticCard}
      >
        {isFetching && type === 'base' && (
          <div className={cn.baseStatisticLoader}>
            <IconLoader />
          </div>
        )}
        <div className={cn.baseStatisticDatepicker}>
          <DateInputRange
            placeholder="Select Date"
            values={baseStatisticDates.map((date) => date.toISOString())}
            onChangeRange={(dates) => {
              setBaseStatisticsDate(dates);
              dispatch(setType('base'));

              let datesQuery = `transferDateFrom=${dates[0].toISOString()}`;

              if (dates.length > 1) {
                datesQuery = `${datesQuery}&transferDateTo=${dates[1].toISOString()}`;
              }

              getBaseStatisticsAction(datesQuery);
            }}
            onReset={() => {
              dispatch(setType('base'));
              setBaseStatisticsDate([]);
              getBaseStatisticsAction();
            }}
          />
        </div>
        <BaseStatistic
          baseStatistic={baseStatistic}
          customersTransactionTypes={customersTransactionTypes}
          baseCurrency={baseCurrency}
        />
      </Card>
      <Currencies
        isLoading={Boolean(!currentBalances)}
        currencies={currentTotalBalance?.currency}
        balance={currentBalances}
      />
      <Card
        title="Profit by Customer"
        className={cardStyle.baseStatisticCard}
        isLoading={isLoading || profitIsLoading}
      >
        {profitIsFetching && type === 'profit' && (
          <div className={cn.baseStatisticLoader}>
            <IconLoader />
          </div>
        )}
        <div className={cn.profitDatepicker}>
          <DateInputRange
            placeholder="Select Date"
            className={cn.profitByCustomerRangePicker}
            values={profitByCustomerDates.map((date) => date.toISOString())}
            onChangeRange={(dates) => {
              setProfitByCustomerDate(dates);
              dispatch(setType('profit'));

              let datesQuery = `transferDateFrom=${dates[0].toISOString()}`;

              if (dates.length > 1) {
                datesQuery = `${datesQuery}&transferDateTo=${dates[1].toISOString()}`;
              }

              getProfitByCustomerAction(datesQuery);
            }}
            onReset={() => {
              dispatch(setType('profit'));
              setProfitByCustomerDate([]);
              getProfitByCustomerAction();
            }}
          />
        </div>
        <ProfitByCustomer
          profitByCustomer={profitByCustomer}
          baseCurrency={baseCurrency}
        />
      </Card>
    </div>
  );
};

export default Statistic;
